import { injectable, inject } from 'inversify';
import { Action } from '@openfin/predux';
import { throttle } from 'lodash';

import { Inject } from '../common/Injectables';
import type {
  NotificationActionEvent,
  NotificationClosedEvent,
  NotificationCreatedEvent,
  NotificationFormSubmittedEvent,
  NotificationsCountChanged,
} from '../../client';
import { ActionTrigger } from '../../client/actions';
import { Transport, Targeted } from '../../client/EventRouter';
import { NotificationActionEventTransport } from '../../client/internal';
import { NotificationSource, NotificationSourceDesktop } from '../../client/source';
import { RootState, mutable } from '../store/State';
import {
  CreateNotification,
  RemoveNotifications,
  ClickButton,
  ClickNotification,
  ExpireNotification,
} from '../store/Actions';
import { StoredNotification } from '../model/StoredNotification';
import { EventPump } from '../model/EventPump';
import { ServiceStore } from '../store/ServiceStore';
import { getSourceApp } from '../view/utils/SourceInfo';
import { SubmitNotificationForm } from '../store/partials/Notification';

/**
 * Translates store actions to events to be dispatched to the client.
 */
@injectable()
export class ClientEventController {
  private readonly _eventPump: EventPump;
  private readonly _store: ServiceStore;

  private readonly _throttledBroadcastNotificationCount = throttle(
    () => this.broadcastNotificationCount(),
    1000
  );

  constructor(
    @inject(Inject.EVENT_PUMP) eventPump: EventPump,
    @inject(Inject.STORE) store: ServiceStore
  ) {
    this._eventPump = eventPump;
    this._store = store;

    store.onAction.add(
      async (action: Action<RootState>): Promise<void> => {
        if (action instanceof CreateNotification) {
          const { notification, source } = action.notification;
          if (source.type === 'desktop') {
            const event: Targeted<Transport<NotificationCreatedEvent>> = {
              target: 'default',
              type: 'notification-created',
              notification: mutable(notification),
            };
            await eventPump.push<NotificationCreatedEvent>(source.identity.uuid, event);
          }
        } else if (action instanceof RemoveNotifications) {
          const { notifications } = action;
          await Promise.all(
            notifications.map(async (storedNotification: StoredNotification) => {
              const { notification, source } = storedNotification;
              if (notification.onClose !== null) {
                const actionEvent: Targeted<Transport<NotificationActionEvent>> = {
                  target: 'default',
                  type: 'notification-action',
                  trigger: ActionTrigger.CLOSE,
                  notification: mutable(notification),
                  source,
                  result: notification.onClose,
                };

                await this.sendAction(actionEvent, source);
              }

              if (source.type === 'desktop') {
                const closedEvent: Targeted<Transport<NotificationClosedEvent>> = {
                  target: 'default',
                  type: 'notification-closed',
                  notification: mutable(notification),
                };
                await eventPump.push<NotificationClosedEvent>(source.identity.uuid, closedEvent);
              }
            })
          );
        } else if (action instanceof ClickButton) {
          const { notification, source } = action.notification;
          const button = notification.buttons[action.buttonIndex];
          if (button.onClick !== null) {
            const event: Targeted<Transport<NotificationActionEvent>> = {
              target: 'default',
              type: 'notification-action',
              trigger: ActionTrigger.CONTROL,
              notification: mutable(notification),
              source,
              controlSource: 'buttons',
              controlIndex: action.buttonIndex,
              result: button.onClick,
            };

            await this.sendAction(event, source);
          }
        } else if (action instanceof ClickNotification) {
          const { notification, source } = action.notification;

          if (notification.onSelect !== null) {
            const event: Targeted<Transport<NotificationActionEvent>> = {
              target: 'default',
              type: 'notification-action',
              trigger: ActionTrigger.SELECT,
              notification: mutable(notification),
              source,
              result: notification.onSelect,
            };

            await this.sendAction(event, source);
          }
        } else if (action instanceof ExpireNotification) {
          const { notification, source } = action.notification;

          if (notification.onExpire !== null) {
            const event: Targeted<Transport<NotificationActionEvent>> = {
              target: 'default',
              type: 'notification-action',
              trigger: ActionTrigger.EXPIRE,
              notification: mutable(notification),
              source,
              result: notification.onExpire,
            };

            await this.sendAction(event, source);
          }
        } else if (action instanceof SubmitNotificationForm) {
          const { form } = action;
          const { notification } = action.notification;
          const source = action.notification.source as NotificationSourceDesktop;
          const event: Targeted<Transport<NotificationFormSubmittedEvent>> = {
            target: 'default',
            type: 'notification-form-submitted',
            notification: mutable(notification),
            form,
          };

          await eventPump.push<NotificationFormSubmittedEvent>(source.identity.uuid, event);
        }

        if (action instanceof CreateNotification || action instanceof RemoveNotifications) {
          this._throttledBroadcastNotificationCount();
        }
      }
    );
  }

  private async broadcastNotificationCount() {
    const countChangedEvent: Targeted<Transport<NotificationsCountChanged>> = {
      target: 'default',
      type: 'notifications-count-changed',
      count: this._store.state.notifications.length,
    };
    this._eventPump.broadcastCountEvent(countChangedEvent);
  }

  private async sendAction(
    event: Targeted<NotificationActionEventTransport>,
    source: NotificationSource
  ): Promise<void> {
    if (source.type === 'desktop') {
      // Send to desktop app
      const uuid: string | null = getSourceApp(source);
      if (uuid) {
        await this._eventPump.push<NotificationActionEvent>(uuid, event);
      } else {
        console.warn("Cannot send action to desktop app, couldn't locate UUID:", event);
      }
    }
  }
}
